import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from './firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from './UserContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import moment from 'moment';
import { Calculator, FileText, CheckCircle, Clock, Trash2, Printer, AlertTriangle, XCircle, ExternalLink } from 'lucide-react';

export default function DoctorsTally() {
    const { userProfile } = useUser();
    const centerId = userProfile?.centerId;

    // Helper per il mese precedente
    const getPrevMonth = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return format(d, 'yyyy-MM');
    };

    // 🎯 STATI INDIPENDENTI PER I MESI
    const [generationMonth, setGenerationMonth] = useState(format(new Date(), 'yyyy-MM'));
    const [viewMonth, setViewMonth] = useState(getPrevMonth()); 
    
    const [selectedDoctorId, setSelectedDoctorId] = useState('ALL');
    const [generating, setGenerating] = useState(false);
    const [selectedTally, setSelectedTally] = useState(null);
    const [localInclusion, setLocalInclusion] = useState({});
// localInclusion = { [detailId]: true|false } — override locale prima del salvataggio

// 🆕 Override locale delle fasce chiuse: { [closedDetailId]: true|false }
    const [localClosedPaid, setLocalClosedPaid] = useState({});

    // Una fascia chiusa è "pagata" (ore non scalate) se spuntata
    const isClosedPaid = (cd) => {
        if (localClosedPaid[cd.id] !== undefined) return localClosedPaid[cd.id];
        return cd.paid === true;
    };

    // Ore effettivamente scalate = somma delle fasce NON pagate
    const getEffectiveClosedHours = (tally) => {
        if (!tally || !Array.isArray(tally.closedDetails)) return 0;
        return tally.closedDetails
            .filter(cd => !isClosedPaid(cd))
            .reduce((sum, cd) => sum + (cd.hours || 0), 0);
    };

    const queryClient = useQueryClient();

    // Ritorna true se il detail è incluso nel totale
    const isDetailIncluded = (d) => {
    if (localInclusion[d.id] !== undefined) return localInclusion[d.id];
    return d.pendingIncluded !== false; // default true se campo mancante (retrocompatibilità)
    };

    // 1. Fetch Medici (Cachato)
    const { data: doctors = [] } = useQuery({
        queryKey: ['doctors', centerId],
        queryFn: async () => {
            const snap = await getDocs(collection(db, 'centers', centerId, 'doctors'));
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        },
        enabled: !!centerId
    });

    // 2. Fetch Cedolini per il mese selezionato
    const { data: rawTallies = [], isLoading: loading } = useQuery({
        queryKey: ['doctorTallies', centerId, viewMonth],
        queryFn: async () => {
            const q = query(collection(db, 'centers', centerId, 'doctorTallies'), where('monthId', '==', viewMonth));
            const snap = await getDocs(q);
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        },
        enabled: !!centerId && !!viewMonth
    });

    // 3. Filtro Veloce (Client-side)
    const tallies = React.useMemo(() => {
        let list = rawTallies;
        if (selectedDoctorId !== 'ALL') {
            list = list.filter(t => t.doctorId === selectedDoctorId);
        }
        return list.sort((a, b) => a.doctorName.localeCompare(b.doctorName));
    }, [rawTallies, selectedDoctorId]);

    const handleGenerate = async (specificDoctorId = null) => {
        // 🎯 Usa generationMonth
        if (!window.confirm(`Vuoi generare i compensi per ${specificDoctorId ? 'questo medico' : 'TUTTI i medici'} per il mese di ${generationMonth}? I dati precedenti verranno aggiornati.`)) return;
        
        setGenerating(true);
        try {
            const functions = getFunctions(db.app, 'europe-west1'); 
            const generatePayroll = httpsCallable(functions, 'generateDoctorsPayroll');
            
            const payload = { centerId, monthId: generationMonth };
            if (specificDoctorId && specificDoctorId !== 'ALL') payload.doctorId = specificDoctorId;

            const result = await generatePayroll(payload);
            alert(`✅ Operazione completata! ${result.data.count} cedolini elaborati.`);
            // Se ho generato lo stesso mese che sto guardando, invalido la cache
            if (generationMonth === viewMonth) {
                queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
            }
        } catch (error) {
            console.error("Errore generazione:", error);
            alert("Errore durante la generazione: " + error.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleToggleInclusion = async (tally, detail) => {
    const newValue = !isDetailIncluded(detail);
    
    // Aggiornamento ottimistico locale
    setLocalInclusion(prev => ({ ...prev, [detail.id]: newValue }));
    
    // Salvataggio su Firestore
    const updatedDetails = tally.details.map(d =>
        d.id === detail.id ? { ...d, pendingIncluded: newValue } : d
    );
    
    setSelectedTally(prev => prev && prev.id === tally.id
        ? { ...prev, details: updatedDetails }
        : prev
    );

    try {
        await updateDoc(
        doc(db, 'centers', centerId, 'doctorTallies', tally.id),
        { details: updatedDetails }
        );
        queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
    } catch (err) {
        console.error("Errore salvataggio inclusione:", err);
        // Rollback ottimistico
        setLocalInclusion(prev => ({ ...prev, [detail.id]: !newValue }));
        alert("Errore durante il salvataggio. Riprova.");
    }
    };

    // 🆕 Ore pagate LIVE (ricalcolate in base alle spunte delle fasce chiuse)
    const getLiveHoursWorked = (tally) => {
        if (!tally || tally.paymentType !== 'hourly') return tally?.hoursWorked || 0;
        const scheduled = tally.scheduledHours ?? tally.hoursWorked ?? 0;
        return Math.max(0, scheduled - getEffectiveClosedHours(tally));
    };

    const calculateLiveTotal = (tally) => {
        if (!tally) return 0;
        // 🆕 Per i medici a ore, la quota base si ricalcola live sulle ore effettivamente pagate
        const liveHourly = tally.paymentType === 'hourly'
            ? getLiveHoursWorked(tally) * (tally.baseRate || 0)
            : (tally.hourlyAmount || 0);
        const base = (tally.fixedAmount || 0) + liveHourly;
        const detailsTotal = tally.details
            .filter(d => isDetailIncluded(d))
            .reduce((sum, d) => sum + (d.doctorCut || 0), 0);
        return base + detailsTotal;
    };

    // 🆕 Toggle "paga queste ore" su una fascia chiusa
    const handleToggleClosedPaid = async (tally, closedDetail) => {
        const newValue = !isClosedPaid(closedDetail);

        // Aggiornamento ottimistico locale
        setLocalClosedPaid(prev => ({ ...prev, [closedDetail.id]: newValue }));

        const updatedClosedDetails = tally.closedDetails.map(cd =>
            cd.id === closedDetail.id ? { ...cd, paid: newValue } : cd
        );

        // 🆕 Aggiorna subito l'oggetto in mano al modale, così i dati sono coerenti anche riaprendolo
        setSelectedTally(prev => prev && prev.id === tally.id
            ? { ...prev, closedDetails: updatedClosedDetails }
            : prev
        );

        try {
            await updateDoc(
                doc(db, 'centers', centerId, 'doctorTallies', tally.id),
                { closedDetails: updatedClosedDetails }
            );
            // 🆕 Rilegge da Firestore: aggiorna le card della lista
            queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
        } catch (err) {
            console.error("Errore salvataggio ore chiuse:", err);
            setLocalClosedPaid(prev => ({ ...prev, [closedDetail.id]: !newValue }));
            alert("Errore durante il salvataggio. Riprova.");
        }
    };

    // 🎯 FUNZIONE RIFIUTA IMPLEMENTATA
    const handleReject = async (tallyId) => {
        if (!window.confirm("Rifiutare questo compenso? Verrà segnato come 'Da Correggere'.")) return;
        await updateDoc(doc(db, 'centers', centerId, 'doctorTallies', tallyId), {
            status: 'rejected',
            rejectedAt: new Date()
        });
        queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
        setSelectedTally(null); setLocalInclusion({}); setLocalClosedPaid({});
    };

    const handleApproveFinal = async (tally) => {
        const finalTotal = calculateLiveTotal(tally);
        const finalDetails = tally.details.map(d => ({
            ...d,
            includedInPayment: isDetailIncluded(d),
            pendingIncluded: isDetailIncluded(d),
            }));

        // 🆕 Congela anche le decisioni sulle fasce chiuse e le ore ricalcolate
        const finalClosedDetails = Array.isArray(tally.closedDetails)
            ? tally.closedDetails.map(cd => ({ ...cd, paid: isClosedPaid(cd) }))
            : [];
        const finalClosedHours = parseFloat(getEffectiveClosedHours(tally).toFixed(2));
        const finalHoursWorked = parseFloat(getLiveHoursWorked(tally).toFixed(2));
        const finalHourlyAmount = tally.paymentType === 'hourly'
            ? parseFloat((finalHoursWorked * (tally.baseRate || 0)).toFixed(2))
            : (tally.hourlyAmount || 0);

        if (!window.confirm(`Approvare il compenso per un totale di € ${finalTotal.toFixed(2)}?`)) return;

        await updateDoc(doc(db, 'centers', centerId, 'doctorTallies', tally.id), {
            status: 'approved',
            totalAmount: finalTotal,
            details: finalDetails,
            closedDetails: finalClosedDetails,   // 🆕
            closedHours: finalClosedHours,       // 🆕 ore effettivamente scalate
            hoursWorked: finalHoursWorked,       // 🆕 ore effettivamente pagate
            hourlyAmount: finalHourlyAmount,     // 🆕 quota base ricalcolata
            approvedAt: new Date(),
            approvedBy: userProfile.uid
        });
        queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
        setSelectedTally(null); setLocalInclusion({}); setLocalClosedPaid({});
    };

    const handleDelete = async (tallyId) => {
        if (!window.confirm("Eliminare definitivamente questo cedolino? Dovrai rigenerarlo.")) return;
        try {
            await deleteDoc(doc(db, 'centers', centerId, 'doctorTallies', tallyId));
            queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
            if (selectedTally && selectedTally.id === tallyId) { setSelectedTally(null); setLocalInclusion({}); setLocalClosedPaid({}) }
        } catch (error) {
            alert("Errore durante l'eliminazione.");
        }
    };

    // 🎯 NUOVA FUNZIONE: Apre la fattura in una nuova scheda senza importare moduli esterni!
    const handleViewInvoice = async (invoiceId) => {
        try {
            let invoice = null; // Dichiariamo la variabile fuori così esiste per tutta la funzione

            // 1. Scarichiamo la fattura 
            const invSnap = await getDoc(doc(db, 'centers', centerId, 'invoices', invoiceId));
            
            if (invSnap.exists()) {
                invoice = invSnap.data();
            } else {
                // Se non c'è, cerchiamo nelle note di credito
                const cnSnap = await getDoc(doc(db, 'centers', centerId, 'credit_notes', invoiceId));
                if (cnSnap.exists()) {
                    invoice = cnSnap.data();
                } else {
                    return alert("Documento non trovato nel database.");
                }
            }
            
            // Scarichiamo i dati del centro
            const centerSnap = await getDoc(doc(db, 'centers', centerId));
            const centerInfo = centerSnap.exists() ? centerSnap.data().billingInfo || {} : {};

            // 2. Prepariamo i dati per la stampa
            const invoiceDate = invoice.date?.toDate ? invoice.date.toDate() : new Date(invoice.date);
            const dateStr = format(invoiceDate, "dd/MM/yyyy");
            const invoiceYear = invoiceDate.getFullYear();
            const numText = invoice.number === 'SCONTRINO' ? 'SCONTRINO' : `N. ${invoice.number}/${invoiceYear}`;
            
            // Creiamo le righe della tabella
            const itemsHtml = (invoice.items || []).map(item => `
                <tr>
                    <td style="padding: 10px 0; border-bottom: 1px solid #e5e7eb;">
                        <strong>${item.qty || 1} x ${item.name}</strong>
                        ${item.notes ? `<div style="font-size: 0.85rem; color: #6b7280; font-style: italic;">Note: ${item.notes}</div>` : ''}
                    </td>
                    <td style="padding: 10px 0; border-bottom: 1px solid #e5e7eb; text-align: right; vertical-align: top;">
                        € ${parseFloat(item.price || 0).toFixed(2)}
                    </td>
                </tr>
            `).join('');

            // 3. Costruiamo l'HTML identico al tuo gestionale clienti
            const html = `
            <!DOCTYPE html>
            <html lang="it">
            <head>
                <meta charset="UTF-8">
                <title>${numText} - ${invoice.patientName}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; color: #111827; max-width: 210mm; margin: 0 auto; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
                    .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
                    .center-info h1 { margin: 0 0 5px 0; font-size: 24px; color: #111827; }
                    .center-info p { margin: 2px 0; font-size: 14px; color: #4b5563; }
                    .doc-info { text-align: right; }
                    .doc-info h2 { margin: 0 0 5px 0; font-size: 20px; color: #2563eb; }
                    .doc-info p { margin: 2px 0; font-size: 14px; }
                    hr { border: 0; border-top: 1px solid #e5e7eb; margin-bottom: 30px; }
                    .patient-info { margin-bottom: 40px; }
                    .patient-info h3 { margin: 0 0 5px 0; font-size: 18px; }
                    .patient-info p { margin: 2px 0; font-size: 14px; color: #4b5563; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                    th { text-align: left; padding: 10px 0; border-bottom: 2px solid #111827; }
                    th.right { text-align: right; }
                    .totals { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
                    .footer { margin-top: 60px; font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 20px; }
                    .btn-print { padding: 10px 20px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: bold; }
                    @media print {
                        body { box-shadow: none; padding: 0; max-width: 100%; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="no-print" style="text-align: right; margin-bottom: 20px;">
                    <button class="btn-print" onclick="window.print()">Stampa / Salva PDF</button>
                </div>
                
                <div class="header">
                    <div class="center-info">
                        <h1>${centerInfo.ragioneSociale || 'Centro Medico'}</h1>
                        <p>${centerInfo.indirizzo || ''}</p>
                        <p>${centerInfo.citta || ''}</p>
                        <p>P.IVA: ${centerInfo.piva || ''} | CF: ${centerInfo.codiceFiscale || ''}</p>
                    </div>
                    <div class="doc-info">
                        <h2>${invoice.type === 'income' ? 'FATTURA' : 'NOTA DI CREDITO'}</h2>
                        <p>N. <b>${invoice.number === 'SCONTRINO' ? 'SCONTRINO' : `${invoice.number}/${invoiceYear}`}</b></p>
                        <p>Data: ${dateStr}</p>
                    </div>
                </div>
                
                <hr />
                
                <div class="patient-info">
                    <p style="font-size: 12px; color: #6b7280; text-transform: uppercase;">Intestato a:</p>
                    <h3>${invoice.patientName || ''}</h3>
                    <p>${invoice.patientAddress || ''}</p>
                    <p>${invoice.patientCap || ''} ${invoice.patientCity || ''}</p>
                    <p>CF: ${invoice.patientCF || invoice.cf || ''}</p>
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th>Descrizione</th>
                            <th class="right">Importo</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsHtml}
                    </tbody>
                </table>
                
                <div class="totals">
                    TOTALE DOCUMENTO: € ${parseFloat(invoice.amount || 0).toFixed(2)}
                </div>
                
                <div class="footer">
                    <p>Pagamento: ${invoice.method || 'Rimessa diretta'}</p>
                    <p>Banca: ${centerInfo.banca || '-'} - IBAN: ${centerInfo.iban || '-'}</p>
                    <p style="margin-top: 10px;">${centerInfo.footerNote || ''}</p>
                </div>
            </body>
            </html>
            `;

            // 4. Apriamo la pagina in una nuova scheda
            const win = window.open('', '_blank');
            win.document.write(html);
            win.document.close();

        } catch(error) {
            console.error("Errore recupero fattura:", error);
            alert("Errore durante l'apertura della fattura.");
        }
    };

    const handlePrintTally = (tally) => {
    const details = tally.details || [];
    const isHourly = tally.paymentType === 'hourly';
    const buildTable = () => {
        if (details.length === 0) return "<p>Nessuna prestazione rilevata.</p>";
        const rows = details.filter(d => isDetailIncluded(d)).map(d => {
            let badge = "";
            let valToDisplay = parseFloat(d.price || 0).toFixed(2);
            if (d.isPartOfCombo) { badge = " <b>[COMBO]</b>"; valToDisplay = parseFloat(d.finalPrice || d.price).toFixed(2); }
            if (d.isCoveredByPackage) { badge = " <b>[PACCHETTO]</b>"; valToDisplay = parseFloat(d.gettoneMedico || d.price).toFixed(2); }

            const statoCell = d.isPaid
                ? '✅ Pagato'
                : d.pendingType === 'fattura_insoluta' ? '🔴 Insoluta'
                : d.pendingType === 'esito_senza_pagamento' ? '⚠️ Esitato s/pag.'
                : d.pendingType === 'accettazione_aperta' ? '🟡 Acc. aperta'
                : d.pendingType === 'mai_accettato' ? '⬜ Mai accettato'
                : '⚠️ Sospeso';

            const fatturaCell = d.invoiceNumber ? `N. ${d.invoiceNumber}` : '-';

            const durataCell = isHourly
                        ? `${d.appointmentMinutes ? (d.appointmentMinutes / 60).toFixed(2) + 'h' : '-'}`
                        : `€ ${valToDisplay}`;

                        const percSpettanzaCells = isHourly
                        ? `<td>€ ${parseFloat(d.price || 0).toFixed(2)}</td>`
                        : `<td>${d.percentage}%</td><td><strong>€ ${parseFloat(d.doctorCut || 0).toFixed(2)}</strong></td>`;

                        return `
                        <tr style="background: white">
                            <td>${format(new Date(d.date), 'dd/MM/yyyy HH:mm')}</td>
                            <td>${d.patientName}</td>
                            <td>${d.serviceName}${badge}</td>
                            <td>${durataCell}</td>
                            ${percSpettanzaCells}
                            <td>${statoCell}</td>
                            <td>${fatturaCell}</td>
                        </tr>
                        `;
        }).join('');

        const tableHeaders = isHourly
  ? `<tr><th>Data</th><th>Paziente</th><th>Prestazione</th><th>Durata</th><th>Importo Paz.</th><th>Stato Pagamento</th><th>N. Fattura</th></tr>`
  : `<tr><th>Data</th><th>Paziente</th><th>Prestazione</th><th>Base Calcolo</th><th>% Medico</th><th>Quota Spettante</th><th>Stato Pagamento</th><th>N. Fattura</th></tr>`;

return `<table><thead>${tableHeaders}</thead><tbody>${rows}</tbody></table>`;
    };

    // 🆕 Riepilogo ore + specchietto sedute chiuse (solo medici a ore)
    const buildHoursSection = () => {
        if (!isHourly) return '';

        const scheduled = tally.scheduledHours ?? tally.hoursWorked ?? 0;
        const closedEff = getEffectiveClosedHours(tally);
        const paidHours = getLiveHoursWorked(tally);
        const cds = Array.isArray(tally.closedDetails) ? tally.closedDetails : [];

        const hoursBox = `
            <div class="hours-summary">
                <table class="hours-table">
                    <tr>
                        <td><span class="lbl">Ore Turno (teoriche)</span><br><span class="val">${scheduled.toFixed(2)}h</span></td>
                        <td><span class="lbl">Ore Scalate</span><br><span class="val neg">−${closedEff.toFixed(2)}h</span></td>
                        <td><span class="lbl">Ore Pagate</span><br><span class="val pos">${paidHours.toFixed(2)}h</span></td>
                        <td><span class="lbl">Tariffa Oraria</span><br><span class="val">€ ${(tally.baseRate || 0).toFixed(2)}/h</span></td>
                        <td><span class="lbl">Quota Base</span><br><span class="val pos">€ ${(paidHours * (tally.baseRate || 0)).toFixed(2)}</span></td>
                    </tr>
                </table>
            </div>`;

        if (cds.length === 0) return hoursBox;

        const closedRows = [...cds]
            .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
            .map(cd => {
                const paid = isClosedPaid(cd);
                return `
                <tr>
                    <td>${moment(cd.date, 'YYYY-MM-DD').format('ddd DD/MM/YYYY')}</td>
                    <td>${cd.startTime} – ${cd.endTime}</td>
                    <td style="text-transform: capitalize">${(cd.reason || 'altro').replace(/_/g, ' ')}</td>
                    <td><strong>${paid ? 'RETRIBUITE' : 'SCALATE'}</strong></td>
                    <td style="text-align:right"><strong>${paid ? '' : '−'}${(cd.hours || 0).toFixed(2)}h</strong></td>
                </tr>`;
            }).join('');

        return `
            ${hoursBox}
            <h3 style="margin-top:25px">Sedute Chiuse</h3>
            <table>
                <thead>
                    <tr><th>Data</th><th>Fascia oraria</th><th>Motivo</th><th>Esito</th><th style="text-align:right">Ore</th></tr>
                </thead>
                <tbody>${closedRows}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="4" style="text-align:right"><strong>Totale ore scalate dal compenso</strong></td>
                        <td style="text-align:right"><strong>−${closedEff.toFixed(2)}h</strong></td>
                    </tr>
                </tfoot>
            </table>
            <h3 style="margin-top:25px">Analitico Prestazioni</h3>`;
    };
    

    const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8"><title>Distinta Compenso</title><style>body { font-family: Arial, sans-serif; padding: 40px; } table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; } th, td { padding: 8px; border: 1px solid #cbd5e1; text-align: left; } th { background-color: #f1f5f9 } .summary { background: #f8fafc; padding: 20px; margin-bottom: 20px; border-left: 4px solid #2563eb } .hours-summary { margin-top: 20px } .hours-table td { text-align: center; background: #f8fafc; padding: 12px } .hours-table .lbl { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: bold } .hours-table .val { font-size: 16px; font-weight: bold; color: #0f172a } .hours-table .neg { color: #dc2626 } .hours-table .pos { color: #16a34a } tfoot td { background: #f8fafc }</style></head><body><h1>Distinta Compensi</h1><p><strong>Medico:</strong> ${tally.doctorName}<br><strong>Mese:</strong> ${tally.monthId}</p><div class="summary"><h2>TOTALE DA LIQUIDARE: € ${calculateLiveTotal(tally).toFixed(2)}</h2></div>${buildHoursSection()}${buildTable()}</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
};

const handleOpenInCrm = (detail) => {
  const appDate = new Date(detail.date);
  const dateStr = appDate.toISOString().split('T')[0]; // "YYYY-MM-DD"
  const patientName = encodeURIComponent(detail.patientName || '');
  const BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5173' 
  : 'https://clinico.auramedical.club';
const url = `${BASE_URL}/crm?tab=appuntamenti&search=${patientName}&dataDa=${dateStr}&dataA=${dateStr}`;
  window.open(url, '_blank');
};
    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            
            <h2 style={{ margin: '0 0 20px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calculator size={24} color="#2563eb" /> Gestione Compensi Medici
            </h2>

            {/* AREA 1: GENERAZIONE (Mese Corrente di Default) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', backgroundColor: '#eff6ff', padding: '20px', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
                <div>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#1e3a8a' }}>🛠 Generazione Nuovi Cedolini</h3>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '5px' }}>Mese da Generare</label>
                            <input 
                                type="month" value={generationMonth} onChange={(e) => setGenerationMonth(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #93c5fd', fontSize: '14px' }}
                            />
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => handleGenerate('ALL')} disabled={generating} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: generating ? 'not-allowed' : 'pointer' }}>
                        <Calculator size={18} /> {generating ? 'Elaborazione...' : 'Genera Tutti'}
                    </button>
                </div>
            </div>

            {/* AREA 2: VISUALIZZAZIONE E FILTRI (Mese Precedente di Default) */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '5px' }}>Visualizza Cedolini del Mese:</label>
                    <input 
                        type="month" value={viewMonth} onChange={(e) => setViewMonth(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '5px' }}>Filtra Medico</label>
                    <select value={selectedDoctorId} onChange={(e) => setSelectedDoctorId(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', minWidth: '200px' }}>
                        <option value="ALL">Tutti i Medici</option>
                        {doctors.map(d => <option key={d.id} value={d.id}>{d.lastName} {d.firstName}</option>)}
                    </select>
                </div>
            </div>

            {/* LISTA CEDOLINI */}
            {loading ? <p>Caricamento in corso...</p> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {tallies.length === 0 ? (
                        <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#94a3b8', backgroundColor: 'white', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                            <FileText size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
                            <p>Nessun compenso trovato per il mese selezionato.</p>
                        </div>
                    ) : tallies.map(tally => {
                        // 🆕 Per i medici a ore, la quota base si ricalcola live in base alle fasce chiuse spuntate
                        const liveHourlyAmount = tally.paymentType === 'hourly'
                            ? getLiveHoursWorked(tally) * (tally.baseRate || 0)
                            : (tally.hourlyAmount || 0);
                        const baseAmount = (tally.fixedAmount || 0) + liveHourlyAmount;
                    const totaleInclusi = baseAmount + (tally.details || [])
                        .filter(d => d.pendingIncluded !== false)
                        .reduce((s, d) => s + (d.doctorCut || 0), 0);
                    const totaleSospesiEsclusi = (tally.details || [])
                        .filter(d => d.pendingIncluded === false)
                        .reduce((s, d) => s + (d.doctorCut || 0), 0);

                    return (
                        <div 
                            key={tally.id} onClick={() => setSelectedTally(tally)}
                            style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', cursor: 'pointer', border: `1px solid ${tally.status === 'approved' ? '#86efac' : tally.status === 'rejected' ? '#fca5a5' : '#e2e8f0'}`, borderTop: `4px solid ${tally.status === 'approved' ? '#22c55e' : tally.status === 'rejected' ? '#ef4444' : '#f59e0b'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>{tally.doctorName}</h3>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 8px', borderRadius: '20px', backgroundColor: tally.status === 'approved' ? '#dcfce7' : tally.status === 'rejected' ? '#fee2e2' : '#fef3c7', color: tally.status === 'approved' ? '#166534' : tally.status === 'rejected' ? '#991b1b' : '#92400e' }}>
                                    {tally.status === 'approved' ? 'APPROVATO' : tally.status === 'rejected' ? 'RIFIUTATO' : 'IN ATTESA'}
                                </span>
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <div style={{ fontSize: '22px', fontWeight: '900', color: '#16a34a' }}>
                                    ✅ € {totaleInclusi.toFixed(2)}
                                </div>
                                {totaleSospesiEsclusi > 0 && (
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626', marginTop: '4px' }}>
                                        ⚠️ + € {totaleSospesiEsclusi.toFixed(2)} sospesi esclusi
                                    </div>
                                )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Clock size={12} /> {tally.details?.length || 0} prestazioni elaborate
                            </div>
                        </div>
                       );
                    })}
                </div>
            )}

            {/* MODALE DI DETTAGLIO */}
            {selectedTally && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => { setSelectedTally(null); setLocalInclusion({}); setLocalClosedPaid({}) }}>
                    <div style={{ backgroundColor: 'white', width: '95%', maxWidth: '1000px', maxHeight: '90vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                        
                        <div style={{ padding: '20px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                            <div>
                                <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Dettaglio Compensi: {selectedTally.doctorName}</h2>
                                <span style={{ color: '#64748b', fontSize: '13px' }}>Mese: {selectedTally.monthId} | Modello: {selectedTally.paymentType}</span>
                            </div>
                            <button onClick={() => { setSelectedTally(null); setLocalInclusion({}); setLocalClosedPaid({}) }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
                        </div>

                        <div style={{ padding: '25px', overflowY: 'auto', flex: 1 }}>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px', marginBottom: '25px' }}>

                                {selectedTally.paymentType === 'hourly' ? (
                                    <>
                                    <div style={{ backgroundColor: '#f1f5f9', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Ore Turno (teoriche)</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a' }}>
                                            {(selectedTally.scheduledHours ?? selectedTally.hoursWorked ?? 0).toFixed(2)}h
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Da turni assegnati</div>
                                    </div>
                                    <div style={{ backgroundColor: (getEffectiveClosedHours(selectedTally) > 0) ? '#fef2f2' : '#f8fafc', padding: '15px', borderRadius: '8px', border: `1px solid ${(getEffectiveClosedHours(selectedTally) > 0) ? '#fecaca' : '#e2e8f0'}` }}>
                                        <div style={{ fontSize: '11px', color: (selectedTally.closedHours > 0) ? '#991b1b' : '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Ore Chiuse</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: (selectedTally.closedHours > 0) ? '#dc2626' : '#94a3b8' }}>
                                            {getEffectiveClosedHours(selectedTally) > 0 ? '−' : ''}{getEffectiveClosedHours(selectedTally).toFixed(2)}h
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Sedute chiuse non retribuite</div>
                                    </div>
                                    <div style={{ backgroundColor: '#ecfdf5', padding: '15px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                                        <div style={{ fontSize: '11px', color: '#065f46', fontWeight: 'bold', textTransform: 'uppercase' }}>Ore Pagate</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>{getLiveHoursWorked(selectedTally).toFixed(2)}h</div>
                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Base calcolo compenso</div>
                                    </div>
                                    <div style={{ backgroundColor: '#eff6ff', padding: '15px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                        <div style={{ fontSize: '11px', color: '#1e40af', fontWeight: 'bold', textTransform: 'uppercase' }}>Ore Produttive</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e3a8a' }}>{selectedTally.productiveHours?.toFixed(2)}h</div>
                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Somma durata appuntamenti</div>
                                    </div>
                                    <div style={{ backgroundColor: '#10b981', padding: '15px', borderRadius: '8px', color: 'white' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.9 }}>Totale da Liquidare</div>
                                        <div style={{ fontSize: '22px', fontWeight: '900' }}>€ {calculateLiveTotal(selectedTally).toFixed(2)}</div>
                                        <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>{getLiveHoursWorked(selectedTally).toFixed(2)}h × € {selectedTally.baseRate?.toFixed(2)}/h</div>
                                    </div>
                                    </>
                                ) : (
                                    <>
                                    {(selectedTally.fixedAmount > 0 || selectedTally.hourlyAmount > 0) && (
                                        <div style={{ backgroundColor: '#f1f5f9', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>Quota Base (Fisso/Ore)</div>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>€ {(selectedTally.fixedAmount + selectedTally.hourlyAmount).toFixed(2)}</div>
                                        </div>
                                    )}
                                    <div style={{ backgroundColor: '#eff6ff', padding: '15px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                        <div style={{ fontSize: '11px', color: '#1e40af', fontWeight: 'bold', textTransform: 'uppercase' }}>Totale Provvigioni</div>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e3a8a' }}>
                                        € {selectedTally.details.filter(d => isDetailIncluded(d)).reduce((s, d) => s + (d.doctorCut || 0), 0).toFixed(2)}
                                        </div>
                                    </div>
                                    <div style={{ backgroundColor: '#10b981', padding: '15px', borderRadius: '8px', color: 'white' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.9 }}>Totale da Liquidare</div>
                                        <div style={{ fontSize: '22px', fontWeight: '900' }}>€ {calculateLiveTotal(selectedTally).toFixed(2)}</div>
                                    </div>
                                    </>
                                )}
                                </div>

                            {/* 🆕 Sedute chiuse: spunta per decidere se retribuire le ore */}
                            {selectedTally.paymentType === 'hourly' && Array.isArray(selectedTally.closedDetails) && selectedTally.closedDetails.length > 0 && (
                                <div style={{ marginBottom: '25px' }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        🚫 Sedute Chiuse
                                        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#64748b' }}>
                                            ({selectedTally.closedDetails.length} {selectedTally.closedDetails.length === 1 ? 'fascia' : 'fasce'} · ore scalate −{getEffectiveClosedHours(selectedTally).toFixed(2)}h)
                                        </span>
                                    </h4>
                                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                                        Spunta una fascia per <b>retribuire</b> comunque quelle ore. Le fasce non spuntate vengono scalate dal compenso.
                                    </p>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                                    <th style={{ padding: '10px 12px', width: '50px', textAlign: 'center' }}>Paga</th>
                                                    <th style={{ padding: '10px 12px' }}>Data</th>
                                                    <th style={{ padding: '10px 12px' }}>Fascia oraria</th>
                                                    <th style={{ padding: '10px 12px' }}>Motivo</th>
                                                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Ore</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[...selectedTally.closedDetails]
                                                    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                                                    .map((cd) => {
                                                    const paid = isClosedPaid(cd);
                                                    return (
                                                    <tr key={cd.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: paid ? '#f0fdf4' : '#fef2f2' }}>
                                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={paid}
                                                                onChange={() => handleToggleClosedPaid(selectedTally, cd)}
                                                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                                title={paid ? 'Ore retribuite' : 'Ore scalate'}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                                                            {moment(cd.date, 'YYYY-MM-DD').format('ddd DD/MM/YYYY')}
                                                        </td>
                                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                                            {cd.startTime} – {cd.endTime}
                                                        </td>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <span style={{ backgroundColor: paid ? '#dcfce7' : '#fee2e2', color: paid ? '#166534' : '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, textTransform: 'capitalize' }}>
                                                                {(cd.reason || 'altro').replace(/_/g, ' ')}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: paid ? '#16a34a' : '#dc2626' }}>
                                                            {paid ? '' : '−'}{(cd.hours || 0).toFixed(2)}h
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                                                    <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, color: '#991b1b', textAlign: 'right' }}>
                                                        Totale ore scalate dal compenso
                                                    </td>
                                                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 900, color: '#dc2626' }}>
                                                        −{getEffectiveClosedHours(selectedTally).toFixed(2)}h
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <h4 style={{ margin: '0 0 15px 0', color: '#334155' }}>Analitico Prestazioni</h4>
                            
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                            <th style={{ padding: '12px', width: '50px', textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTally.details.length > 0 && selectedTally.details.every(d => isDetailIncluded(d))}
                                                    onChange={async () => {
                                                        const allIncluded = selectedTally.details.every(d => isDetailIncluded(d));
                                                        const newValue = !allIncluded;
                                                        // Aggiornamento ottimistico locale
                                                        const newLocalInclusion = {};
                                                        selectedTally.details.forEach(d => { newLocalInclusion[d.id] = newValue; });
                                                        setLocalInclusion(newLocalInclusion);
                                                        // Salvataggio su Firestore
                                                        const updatedDetails = selectedTally.details.map(d => ({ ...d, pendingIncluded: newValue }));
                                                        // 🆕 Aggiorna subito l'oggetto del modale
                                                        setSelectedTally(prev => prev ? { ...prev, details: updatedDetails } : prev);
                                                        try {
                                                            await updateDoc(
                                                                doc(db, 'centers', centerId, 'doctorTallies', selectedTally.id),
                                                                { details: updatedDetails }
                                                            );
                                                            // 🆕 Aggiorna le card della lista senza refresh
                                                            queryClient.invalidateQueries({ queryKey: ['doctorTallies', centerId, viewMonth] });
                                                        } catch (err) {
                                                            console.error("Errore salvataggio:", err);
                                                            setLocalInclusion({});
                                                            alert("Errore durante il salvataggio. Riprova.");
                                                        }
                                                    }}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563eb' }}
                                                    title="Seleziona/Deseleziona tutti"
                                                />
                                            </th>
                                            <th style={{ padding: '12px' }}>Data</th>
                                            <th style={{ padding: '12px' }}>Paziente</th>
                                            <th style={{ padding: '12px' }}>Servizio</th>
                                            <th style={{ padding: '12px' }}>
                                            {selectedTally.paymentType === 'hourly' ? 'Durata' : 'Valore'}
                                            </th>
                                            {selectedTally.paymentType !== 'hourly' && (
                                            <>
                                                <th style={{ padding: '12px', textAlign: 'center' }}>%</th>
                                                <th style={{ padding: '12px' }}>Spettanza</th>
                                            </>
                                            )}
                                            {selectedTally.paymentType === 'hourly' && (
                                            <th style={{ padding: '12px' }}>Importo Paz.</th>
                                            )}
                                            <th style={{ padding: '12px' }}>Stato</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Fatt.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedTally.details.map((d, i) => {
                                            const isExcluded = !isDetailIncluded(d);
                                            
                                            // 🎯 FIX: Mostriamo graficamente la provenienza del compenso
                                            let serviceBadge = null;
                                            let displayedBasePrice = d.price; // Default: Il paziente ha pagato X
                                            
                                            if (d.isPartOfCombo) {
                                                serviceBadge = <span style={{ fontSize: '10px', backgroundColor: '#f3e8ff', color: '#7c3aed', padding: '2px 6px', borderRadius: '10px', marginLeft: '6px', border: '1px solid #d8b4fe' }}>⚡ Combo</span>;
                                                displayedBasePrice = d.finalPrice || d.price; // La quota pro-rata del Combo
                                            } else if (d.isCoveredByPackage) {
                                                serviceBadge = <span style={{ fontSize: '10px', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: '10px', marginLeft: '6px', border: '1px solid #86efac' }}>📦 Pacchetto</span>;
                                                // 💰 Il paziente ha pagato 0 in cassa, ma il medico è pagato sul "gettone" salvato!
                                                displayedBasePrice = d.gettoneMedico || d.price; 
                                            }

                                            return (
                                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: isExcluded ? '#fff1f2' : 'transparent', transition: 'background 0.2s' }}>
                                                    <td style={{ textAlign: 'center', padding: '10px' }}>
                                                        <input 
                                                            type="checkbox"
                                                            checked={isDetailIncluded(d)}
                                                            onChange={() => handleToggleInclusion(selectedTally, d)}
                                                            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563eb' }}
                                                            />
                                                    </td>
                                                    <td style={{ padding: '10px', whiteSpace: 'nowrap', color: '#475569', fontSize: '12px' }}>{format(new Date(d.date), 'dd/MM/yy HH:mm')}</td>
                                                    <td style={{ padding: '10px', fontWeight: '600', color: '#1e293b' }}>{d.patientName}</td>
                                                    <td style={{ padding: '10px', color: '#334155' }}>
                                                        {d.serviceName} 
                                                        {d.isException && <span title="Eccezione Contrattuale" style={{ cursor: 'help' }}>🔥</span>}
                                                        {serviceBadge}
                                                    </td>
                                                    {selectedTally.paymentType === 'hourly' ? (
                                                        <>
                                                            <td style={{ padding: '10px', fontWeight: '600', color: '#475569' }}>
                                                                {d.appointmentMinutes ? `${(d.appointmentMinutes / 60).toFixed(2)}h` : '-'}
                                                            </td>
                                                            <td style={{ padding: '10px', fontWeight: '600', color: '#475569' }}>
                                                                € {parseFloat(d.price || 0).toFixed(2)}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td style={{ padding: '10px' }}>
                                                                {d.isCoveredByPackage ? (
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontSize: '10px', textDecoration: 'line-through', color: '#94a3b8' }}>€ {parseFloat(d.price || 0).toFixed(2)}</span>
                                                                        <span style={{ fontWeight: 'bold', color: '#166534' }} title="Valore Gettone a Seduta">€ {parseFloat(displayedBasePrice || 0).toFixed(2)}</span>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ fontWeight: '600', color: '#475569' }}>€ {parseFloat(displayedBasePrice || 0).toFixed(2)}</span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'center', color: '#64748b', fontWeight: 'bold' }}>{d.percentage}%</td>
                                                            <td style={{ padding: '10px', fontWeight: '900', color: isExcluded ? '#94a3b8' : '#2563eb', fontSize: '14px' }}>€ {parseFloat(d.doctorCut || 0).toFixed(2)}</td>
                                                        </>
                                                    )}
                                                   
                                                        <td style={{ padding: '10px' }}>
                                                        {d.isPaid ? (
                                                            <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#dcfce7', padding: '3px 6px', borderRadius: '4px' }}>
                                                            ✅ Pagato
                                                            </span>
                                                        ) : d.pendingType === 'fattura_insoluta' ? (
                                                            <div>
                                                            <span style={{ color: '#dc2626', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#fee2e2', padding: '3px 6px', borderRadius: '4px', display: 'block' }}>
                                                                🔴 Fattura Insoluta
                                                            </span>
                                                            {d.invoiceNumber && (
                                                                <span style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                                                Fatt. N. {d.invoiceNumber}
                                                                </span>
                                                            )}
                                                            </div>
                                                        ) : d.pendingType === 'esito_senza_pagamento' ? (
                                                            <span style={{ color: '#d97706', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#fef3c7', padding: '3px 6px', borderRadius: '4px' }}>
                                                            ⚠️ Esitato s/pagamento
                                                            </span>
                                                        ) : d.pendingType === 'accettazione_aperta' ? (
                                                            <span style={{ color: '#7c3aed', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#f3e8ff', padding: '3px 6px', borderRadius: '4px' }}>
                                                            🟡 Accettazione aperta
                                                            </span>
                                                        ) : d.pendingType === 'mai_accettato' ? (
                                                            <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#f1f5f9', padding: '3px 6px', borderRadius: '4px' }}>
                                                                ⬜ Mai accettato
                                                            </span>
                                                        ): (  
                                                            <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#fee2e2', padding: '3px 6px', borderRadius: '4px' }}>
                                                            ⚠️ Sospeso
                                                            </span>
                                                        )}
                                                        </td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                            {d.invoiceId ? (
                                                            <button
                                                                onClick={() => handleViewInvoice(d.invoiceId)}
                                                                style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '4px', borderRadius: '6px' }}
                                                                title="Vedi Fattura / Ricevuta Interna"
                                                            >
                                                                <FileText size={16} />
                                                            </button>
                                                            ) : (
                                                            <span style={{ color: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }} title="Nessun documento associato">
                                                                <FileText size={16} />
                                                            </span>
                                                            )}
                                                            {(d.pendingType === 'accettazione_aperta' || d.pendingType === 'mai_accettato') && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleOpenInCrm(d); }}
                                                                style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '4px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', gap: '2px' }}
                                                                title="Apri nel CRM Clinico"
                                                            >
                                                                <ExternalLink size={12} /> CRM
                                                            </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div style={{ padding: '20px 30px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button onClick={() => handleDelete(selectedTally.id)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '10px', background: 'none', border: 'none', color: '#ef4444', fontWeight: 'bold', cursor: 'pointer' }}>
                                <Trash2 size={16} /> Elimina Conteggio
                            </button>
                            
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button onClick={() => handlePrintTally(selectedTally)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: 'white', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                                    <Printer size={16} /> Stampa
                                </button>
                                
                                {selectedTally.status !== 'approved' && (
                                    <>
                                        <button onClick={() => handleReject(selectedTally.id)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 15px', backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                                            <XCircle size={16} /> Rifiuta
                                        </button>

                                        <button onClick={() => handleApproveFinal(selectedTally)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 25px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(16,185,129,0.2)' }}>
                                            <CheckCircle size={18} /> Approva e Archivia
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}